import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { User } from 'oidc-client-ts';
import { runtimeConfig } from '../app/config';
import { LOGIN_CALLBACK_PATH, createUserManager } from './userManager';

/**
 * A permission is the OAuth scope itself, spelled the same way here, in Authentik and in the
 * backend that enforces it. The portal once carried its own names and translated them; the
 * translation was a second place for the two vocabularies to drift apart without anything failing
 * loudly. Each fachsystem owns its own prefix, and since 25.09.2026 that prefix is the service's
 * name - `dam`, `docs`, `std`, `mail`; only sftpgo still grants `sftp`. dam used `media` and std
 * `masterdata` before; a claim carrying those is now dropped here like any unknown name.
 */
// One list; the type is derived from it, so a permission cannot be in one and missing from the other.
export const knownPermissions = [
  'dam.read', 'dam.write', 'dam.export', 'dam.admin',
  'docs.read', 'docs.write', 'docs.analytics', 'docs.admin',
  'sftp.read', 'sftp.write', 'sftp.webhooks', 'sftp.admin',
  'std.read', 'std.write', 'std.edit', 'std.manage', 'std.admin',
  // Sends staged master-data changes to Synfoxx. A capability group of its own, like apidocs.
  'std.upstream-update',
  'mail.read', 'mail.write', 'mail.send', 'mail.admin',
  // The API reference per service. Granted by `<service>-apidocs` alone, never by a role group.
  'dam.apidocs', 'docs.apidocs', 'std.apidocs', 'mail.apidocs', 'sftp.apidocs',
] as const;

export type Permission = (typeof knownPermissions)[number];

export interface Session {
  displayName: string;
  roles: string[];
  permissions: Permission[];
  demo: boolean;
}

export interface DevCredentials {
  username: string;
  password: string;
}

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  error: string | null;
  /**
   * The access token has expired and renewal did not replace it.
   *
   * Separate from `session` on purpose: the shell stays mounted so unsaved input survives, while
   * `expired` is what puts the dialog in front of it. Before this existed the two were the same
   * thing, and an expired session was indistinguishable from a working one - the UI kept rendering
   * and every request went out without an `Authorization` header.
   */
  expired: boolean;
  /** Tries renewal once, and only falls back to the provider when that fails. */
  reauthenticate: () => Promise<void>;
  login: (credentials?: DevCredentials) => Promise<void>;
  logout: () => Promise<void>;
  token: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const devSessionKey = 'ges.portal.dev-user';

function devSession(displayName: string): Session {
  return {
    displayName,
    roles: ['PortalDemoAdmin'],
    permissions: [...knownPermissions],
    demo: true,
  };
}

function storedDevSession(): Session | null {
  if (!runtimeConfig.demoMode) return null;
  const displayName = window.sessionStorage.getItem(devSessionKey)?.trim();
  return displayName ? devSession(displayName) : null;
}

/**
 * Strings only. `value.map(String)` used to stringify whatever it found, so a one-element nested
 * array and an object with a `toString` both produced a valid permission name - measured:
 * `permissions: [['dam.read']]` and `[{ toString: () => 'dam.read' }]` each granted it.
 * Coercion is how a malformed claim becomes an authorization decision.
 */
function claimValues(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((entry): entry is string => typeof entry === 'string');
  return typeof value === 'string' ? value.split(/[ ,]+/).filter(Boolean) : [];
}

/** Exported for the test that guards the claim shapes Authentik actually emits. */
export function sessionFrom(user: User): Session {
  // Authentik's default `profile` mapping emits the group names as `groups`; `roles` only appears
  // when the `entitlements` scope is attached, which this provider does not use. Reading both
  // keeps the portal working against either shape rather than silently finding nothing.
  const roles = [
    ...claimValues(user.profile.groups),
    ...claimValues(user.profile.roles ?? user.profile.role),
  ];
  // `permissions` is the contract, and it is the only source. Deliberately NOT `scope`: Authentik
  // hands out every scope the portal asks for whatever the user's groups say, so `scope` would
  // give every signed-in user all twenty-one. And deliberately not a local group table either -
  // the portal carried one, it drifted from the blueprint in three groups without anything going
  // red, and a table unioned with the claim can only ever grant MORE than the backend allows.
  // The IdP computes the claim from group membership; the backends read the same claim.
  //
  // An absent claim therefore means no permissions, not a fallback. Until the blueprint is applied
  // and `ges.portal` is requested nobody sees a module - which is the same thing the backends do,
  // and the alternative would be a shell offering what every request is refused.
  const permissions = new Set<Permission>();
  for (const value of claimValues(user.profile.permissions)) {
    const known = knownPermissions.find((permission) => permission === value);
    if (known) permissions.add(known);
  }
  if (user.profile.permissions === undefined) {
    // Names the cause where it is visible. The three it distinguishes are in idp-server's README.
    console.warn(
      'OIDC: the token carries no `permissions` claim - the `ges.portal` scope was not requested, '
      + 'or the portal blueprint is not applied. No module will be visible.',
    );
  }
  return {
    displayName: user.profile.name || user.profile.preferred_username || user.profile.email || 'GES Benutzer',
    roles,
    permissions: [...permissions],
    demo: false,
  };
}

export function OidcProvider({ children }: PropsWithChildren) {
  const manager = useMemo(() => createUserManager(), []);
  const [session, setSession] = useState<Session | null>(storedDevSession);
  const [loading, setLoading] = useState(Boolean(manager));
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!manager) return;
    let active = true;
    const load = async () => {
      try {
        const callback = window.location.pathname === LOGIN_CALLBACK_PATH;
        const user = callback ? await manager.signinRedirectCallback() : await manager.getUser();
        if (!active) return;
        setSession(user && !user.expired ? sessionFrom(user) : null);
        if (callback) {
          const state = user?.state;
          const returnUrl = typeof state === 'object' && state !== null && 'returnUrl' in state
            && typeof state.returnUrl === 'string' && state.returnUrl.startsWith('/')
            ? state.returnUrl
            : '/';
          window.history.replaceState({}, document.title, returnUrl);
        }
      } catch {
        if (active) setError('Die Anmeldung konnte nicht abgeschlossen werden. Bitte erneut versuchen.');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    const onLoaded = (user: User) => { setSession(sessionFrom(user)); setExpired(false); };
    const onUnloaded = () => setSession(null);
    // The expiry timer, not the renewal error, is what the UI keys on. A renewal that fails at
    // `exp - 60s` may still be followed by a successful one, and the current token is valid until
    // `exp` either way - reacting to the error would put the dialog up while nothing is wrong.
    const onExpired = () => setExpired(true);
    // Not rendered anywhere: this is the observation point. A renewal failure is otherwise silent,
    // and whoever has to diagnose "signed in but every call is a 401" has nothing to look at.
    const onRenewError = (renewError: Error) => console.warn('OIDC silent renew failed', renewError);
    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    manager.events.addAccessTokenExpired(onExpired);
    manager.events.addSilentRenewError(onRenewError);
    return () => {
      active = false;
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeUserUnloaded(onUnloaded);
      manager.events.removeAccessTokenExpired(onExpired);
      manager.events.removeSilentRenewError(onRenewError);
    };
  }, [manager]);

  const reauthenticate = useCallback(async () => {
    if (!manager) return;
    try {
      // Usually enough: the renewal failed because the machine was asleep or the network was gone,
      // and by the time someone clicks, both are back. When it works the dialog closes through
      // `userLoaded` and whatever was typed into a form is still there.
      await manager.signinSilent();
    } catch {
      // The grant itself is gone - refresh token past its validity, session revoked, user deleted.
      // Only the provider can settle that, so this leaves the page.
      await manager.signinRedirect({ state: { returnUrl: window.location.pathname + window.location.search } });
    }
  }, [manager]);

  const login = useCallback(async (credentials?: DevCredentials) => {
    setError(null);
    if (runtimeConfig.demoMode) {
      const username = credentials?.username.trim();
      if (!username || !credentials?.password) {
        setError('Für den lokalen Login werden Benutzername und Passwort benötigt.');
        return;
      }
      window.sessionStorage.setItem(devSessionKey, username);
      setSession(devSession(username));
      return;
    }
    if (manager) await manager.signinRedirect({ state: { returnUrl: window.location.pathname + window.location.search } });
  }, [manager]);

  const logout = useCallback(async () => {
    if (runtimeConfig.demoMode) {
      window.sessionStorage.removeItem(devSessionKey);
      setSession(null);
      return;
    }
    if (manager) await manager.signoutRedirect();
  }, [manager]);

  const token = useCallback(async () => {
    if (runtimeConfig.demoMode) return 'demo-token';
    const user = manager ? await manager.getUser() : null;
    return user && !user.expired ? user.access_token : undefined;
  }, [manager]);

  const value = useMemo<AuthContextValue>(
    () => ({ session, loading, error, expired, reauthenticate, login, logout, token }),
    [session, loading, error, expired, reauthenticate, login, logout, token]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside OidcProvider');
  return value;
}

export function canUsePermission(session: Session, permission: Permission) {
  return session.permissions.includes(permission);
}
